import { approvedAttendance } from "../../domain/work-logs.js";
import { S, ui } from "../../app/store.js";
import {
  MINW,
  REGION_NAME,
  contrib,
  dayKind,
  depPay,
  minWage,
  runLines,
} from "../../domain/payroll.js";
import {
  addDays,
  daysTo,
  fmtD,
  shortD,
  today,
  wd,
} from "../../domain/dates.js";
import {
  can,
  canDepAct,
  canInvoice,
  depMoney,
  emp,
} from "../../services/access.js";
import { proj, siteCode } from "../sites/model.js";
import { esc, pad2, peso } from "../../domain/format.js";
import { chip, csvBtn, panel, seg, tile } from "../../components/ui.js";
import { V } from "../../app/pages.js";
import { client } from "../invoices/model.js";

const DEP_TABS = [
  ["overview", "Overview"],
  ["personnel", "Deployments"],
  ["attendance", "Daily time record"],
  ["billing", "Billing & margin"],
  ["compliance", "Compliance"],
];

const DEP_SUB = {
  overview:
    "Who is deployed where, and this cut-off's man-days, cost and billing.",
  personnel:
    "Each deployment: site, pay and bill rates, contract and safety orientation.",
  attendance:
    "Tick each day worked. Rest days, holidays and wage-order changes apply by date.",
  billing:
    "What each site earns at the bill rates against what its people cost.",
  compliance:
    "The labor, safety and tax duties that come with deploying people.",
};

const depCut = () =>
  S.runs.find((x) => x.id === ui.dcut) ||
  S.runs.find((x) => x.status === "DRAFT") ||
  S.runs[S.runs.length - 1];

const depActiveIn = (from, to) =>
  S.deployments.filter(
    (d) => d.from <= to && (!d.endedOn || d.endedOn >= from),
  );

function syncDraft() {
  S.runs
    .filter((r) => r.status === "DRAFT")
    .forEach((r) => (r.lines = runLines(S, r.from, r.to)));
}

function depCost(x) {
  const c = contrib(x.gross * 2);
  return (
    x.gross +
    Math.round((c.sssER + c.ec + c.phER + c.hdER) / 2) +
    x.allow +
    Math.round(x.base13 / 12)
  );
}

function depAlerts() {
  const out = [],
    t = today(),
    co = S.company;
  if (co.dole?.status !== "REGISTERED")
    out.push({
      t: "Milltech is not yet a DOLE-registered contractor",
      s: "Needed before deploying people to do a client's work (DO 174-17). Register with DOLE Regional Office VI, Iloilo City.",
      tone: "bad",
    });
  S.deployments
    .filter((d) => d.status === "ACTIVE")
    .forEach((d) => {
      const e = emp(d.emp),
        p = proj(d.project),
        mw = minWage(p.region, t),
        left = daysTo(d.to);
      if (d.type === "DAILY" && d.rate < mw.daily)
        out.push({
          t: `${e.name}: ${peso(d.rate)} a day is below the ${peso(mw.daily)} minimum at ${p.site}`,
          s: `${mw.src}. Payroll already pays the minimum; update the contract rate.`,
          tone: "bad",
          act: "dep-raise",
          id: d.id,
          label: `Raise to ${peso(mw.daily)}`,
        });
      if (d.type === "PIECE" && Math.round(d.rate * d.std) < mw.daily)
        out.push({
          t: `${e.name}: ${peso(d.rate)} per ${d.unit} pays ${peso(Math.round(d.rate * d.std))} at standard output`,
          s: `Below the ${peso(mw.daily)} minimum. Labor Code Art. 101 requires piece rates that give a worker of normal ability the minimum wage.`,
          tone: "bad",
        });
      if (!d.contract)
        out.push({
          t: `${e.name} has no signed project employment contract for ${d.project}`,
          s: "Labor Code Art. 295: one written contract per project, naming the project and its expected end",
          tone: "bad",
          act: "dep-contract",
          id: d.id,
          label: "Mark signed",
        });
      if (!e.osh)
        out.push({
          t: `${e.name} hasn't had the mandatory OSH orientation`,
          s: "RA 11058 and DOLE DO 198-18: before work starts at the site",
          tone: "bad",
          act: "dep-osh",
          id: e.id,
          label: "Mark done",
        });
      if (left < 0)
        out.push({
          t: `${e.name} is still working ${-left} day${left === -1 ? "" : "s"} past the contract end (${fmtD(d.to)})`,
          s: "Extend the project contract in writing or end the deployment. Open-ended work risks regular employment.",
          tone: "bad",
          act: "dep-extend",
          id: d.id,
          label: "Extend 30 days",
        });
      else if (left <= 14)
        out.push({
          t: `${e.name}'s deployment at ${p.site} ends ${fmtD(d.to)}`,
          s: "Extend it in writing or plan the end and the DOLE report",
          tone: "warn",
          act: "dep-extend",
          id: d.id,
          label: "Extend 30 days",
        });
      if (!e.tin)
        out.push({
          t: `${e.name} has no TIN on file`,
          s: "Needed for withholding, Form 2316 and the alphalist. File BIR Form 1902.",
          tone: "warn",
        });
    });
  S.deployments
    .filter((d) => d.status === "ENDED" && !d.report)
    .forEach((d) => {
      const due = addDays(d.endedOn, 30);
      out.push({
        t: `Report the end of ${emp(d.emp).name}'s project employment to DOLE`,
        s: `Project ended ${fmtD(d.endedOn)}; file by ${fmtD(due)}. A missing report is read as a sign of regular employment.`,
        tone: daysTo(due) <= 7 ? "bad" : "warn",
        act: "dep-report",
        id: d.id,
        label: "Mark filed",
      });
    });
  return out;
}

const cutSeg = () =>
  seg(
    "dcut",
    S.runs.map((x) => [x.id, `${shortD(x.from)} – ${shortD(x.to)}`]),
    depCut().id,
  );

const alertRows = (list) =>
  list
    .map(
      (a) =>
        `<div><div><strong>${esc(a.t)}</strong><div class="small muted">${esc(a.s)}</div></div><div class="row">${a.act && canDepAct(a.act) ? `<button data-act="${a.act}" data-id="${esc(a.id)}">${esc(a.label)}</button>` : chip(a.tone === "bad" ? "Act now" : "Soon", a.tone)}</div></div>`,
    )
    .join("");

V.deploy = () => {
  if (!S.runs.length)
    return `<section class="panel"><h2>Set up the first payroll cutoff</h2><p>Add your locations, clients and projects, then create a payroll cutoff to begin personnel deployments and daily work tracking.</p><button data-go="payroll">Open HR Payroll</button></section>`;
  const tab = ui.dtab || "overview",
    body = {
      overview: dpOverview,
      personnel: dpPersonnel,
      attendance: dpAttendance,
      billing: dpBilling,
      compliance: dpCompliance,
    }[tab]();
  return `<div class="top" style="margin-top:-12px"><span class="muted">${DEP_SUB[tab]}</span><div class="row">${can("dep.manage") && !ui.depDraft ? `<button class="pri" data-act="dep-new">Deploy personnel</button>` : ""}</div></div>
  <div class="vtabs" role="tablist"><div>${DEP_TABS.map(([v, l]) => `<button role="tab" aria-selected="${v === tab}" data-seg="dtab" data-val="${v}">${l}</button>`).join("")}</div></div>${ui.depDraft ? depForm() : ""}${body}`;
};

function dpOverview() {
  const r = depCut(),
    m = depMoney(),
    act = S.deployments.filter((d) => d.status === "ACTIVE"),
    rows = depActiveIn(r.from, r.to).map((d) => ({
      d,
      x: depPay(S, d, r.from, r.to),
    }));
  const md = rows.reduce((a, o) => a + o.x.worked, 0),
    bill = rows.reduce((a, o) => a + o.x.bill, 0),
    cost = rows.reduce((a, o) => a + depCost(o.x), 0),
    al = depAlerts(),
    sites = [...new Set(rows.map((o) => o.d.project))];
  return `${cutSeg()}<div class="tiles">${tile("Personnel deployed", pad2(act.length), "g")}${tile("Sites", pad2(new Set(act.map((d) => d.project)).size), "g")}${tile("Man-days this cut-off", String(md), "g")}${m ? tile("Margin this cut-off", peso(bill - cost), bill > cost ? "g" : "r") : ""}</div>
  <div class="grid"><section class="panel"><div class="panel-h"><h2>Needs attention</h2>${al.length ? chip(`${al.length} open`, "bad") : chip("All clear", "good")}</div><div class="inbox">${alertRows(al.slice(0, 6)) || `<div><span class="muted">Nothing needs attention.</span></div>`}${al.length > 6 ? `<div><button class="ghost link" data-seg="dtab" data-val="compliance">See all ${al.length}</button></div>` : ""}</div></section>
   <section class="panel"><div class="panel-h"><h2>By site</h2><span class="muted">${fmtD(r.from)} – ${fmtD(r.to)}</span></div><div class="inbox">${
     sites
       .map((c) => {
         const p = proj(c),
           os = rows.filter((o) => o.d.project === c),
           b = os.reduce((a, o) => a + o.x.bill, 0),
           k = os.reduce((a, o) => a + depCost(o.x), 0);
         return `<div><div><strong>${esc(c)}</strong> <span class="muted">${esc(p.site)}</span><div class="small muted">${os.length} personnel · ${os.reduce((a, o) => a + o.x.worked, 0)} man-days · minimum ${peso(minWage(p.region, r.to).daily)} a day</div></div>${m ? `<div style="text-align:right"><span class="num">${peso(b - k)}</span><div class="small muted">margin on ${peso(b)}</div></div>` : ""}</div>`;
       })
       .join("") ||
     `<div><span class="muted">Nobody is deployed in this cut-off.</span></div>`
   }</div></section></div>`;
}

function dpPersonnel() {
  const m = depMoney(),
    f = ui.depf || "ACTIVE",
    list = S.deployments
      .filter((d) => f === "all" || d.status === f)
      .slice()
      .reverse();
  return panel(
    "Deployments",
    `${S.deployments.filter((d) => d.status === "ACTIVE").length} active`,
    csvBtn("t-dep"),
    `${seg(
      "depf",
      [
        ["ACTIVE", "Active"],
        ["ENDED", "Ended"],
        ["all", "All"],
      ],
      f,
    )}
  <div class="tbl"><table id="t-dep"><thead><tr><th>Person</th><th>Project · site</th><th>Period</th><th class="n">Pay rate</th><th class="n">Bill rate</th><th>Papers</th><th></th></tr></thead><tbody>
  ${
    list
      .map((d) => {
        const e = emp(d.emp),
          p = proj(d.project),
          left = daysTo(d.to),
          mw = minWage(p.region, today()),
          per =
            d.status === "ENDED"
              ? chip("Ended " + fmtD(d.endedOn), "mut")
              : left < 0
                ? chip(`${-left} days past end`, "bad")
                : left <= 14
                  ? chip(`Ends in ${left} days`, "warn")
                  : chip(`${left} days left`, "good"),
          unit = d.type === "DAILY" ? "day" : esc(d.unit);
        return `<tr><td><strong>${esc(e.name)}</strong><div class="small muted">${esc(e.position)} · ${d.type === "DAILY" ? "daily rate" : "piece rate"}</div></td><td>${esc(d.project)} ${esc(p.name)}<div class="small muted">${esc(p.site)} · ${esc(REGION_NAME[p.region])}</div></td>
    <td class="small">${shortD(d.from)} – ${fmtD(d.to)}<div>${per}</div></td>
    <td class="n">${m ? `${peso(d.rate)}/${unit}${d.type === "DAILY" && d.rate < mw.daily ? `<div>${chip("Below minimum", "bad")}</div>` : ""}${d.type === "PIECE" ? `<div class="small muted">${d.std} a day = ${peso(Math.round(d.rate * d.std))}</div>` : ""}${d.perDiem ? `<div class="small muted">+ ${peso(d.perDiem)} site allowance a day</div>` : ""}` : "••••"}</td>
    <td class="n">${m ? `${peso(d.bill)}/${unit}` : "••••"}</td>
    <td><div class="row">${d.contract ? chip("Contract", "good") : chip("No contract", "bad")}${e.osh ? chip("OSH", "good") : chip("No OSH", "bad")}${d.status === "ENDED" ? (d.report ? chip("DOLE report", "good") : chip("Report due", "warn")) : ""}</div></td>
    <td><div class="row" style="flex-wrap:nowrap">${d.status === "ACTIVE" && can("dep.manage") ? `<button class="ghost" data-act="dep-extend" data-id="${d.id}">Extend</button><button class="ghost" data-act="dep-end" data-id="${d.id}">End</button>` : ""}</div></td></tr>`;
      })
      .join("") ||
    `<tr><td colspan="7" class="muted">No deployments here.</td></tr>`
  }
  </tbody></table></div><p class="small muted">Each deployment is project employment (Labor Code Art. 295): one written contract per project with its expected end. Ending a deployment starts the DOLE termination report.</p>`,
  );
}

function dpAttendance() {
  const r = depCut(),
    editable = false,
    m = depMoney(),
    deps = depActiveIn(r.from, r.to),
    t = today(),
    dates = [];
  for (let x = r.from; x <= r.to; x = addDays(x, 1)) dates.push(x);
  const head = dates
    .map((x) => {
      const k = dayKind(x, 0);
      return `<th class="n" title="${k === "REG" ? "Regular holiday" : k === "SPC" ? "Special day" : k === "REST" ? "Rest day" : "Work day"}"${k !== "WORK" ? ' style="color:var(--accent)"' : ""}>${+x.slice(8)}<br>${"SMTWTFS"[wd(x)]}</th>`;
    })
    .join("");
  const note =
    r.status === "DRAFT"
      ? editable
        ? "Tick each day worked; the payroll draft recomputes as you go"
        : "Approved work logs · record and approve changes in Work logs"
      : `Locked: payroll ${r.id} is ${r.status.toLowerCase()}`;
  return `${cutSeg()}${panel(
    `Daily time record · ${r.id}`,
    `${fmtD(r.from)} – ${fmtD(r.to)} · ${note}`,
    `<button data-go="worklogs">Open work logs</button>` + csvBtn("t-dtr"),
    `<div class="tbl"><table id="t-dtr" class="dtr"><thead><tr><th>Person · site</th>${head}<th class="n">OT h</th><th class="n">Night h</th><th class="n">Output</th><th class="n">Days</th>${m ? `<th class="n">Gross</th>` : ""}</tr></thead><tbody>
   ${
     deps
       .map((d) => {
         const e = emp(d.emp),
           a = approvedAttendance(S, d, r.from, r.to),
           x = depPay(S, d, r.from, r.to),
           end = d.endedOn || "9999-12-31";
         const cells = dates
           .map((dt) => {
             if (dt < d.from || dt > end) return `<td class="n muted">·</td>`;
             const on = a.dates.includes(dt),
               k = dayKind(dt, d.rest ?? 0);
             return `<td class="n"><button class="day${on ? " on" : ""}${k !== "WORK" ? " k" : ""}" ${!editable || dt > t ? "disabled" : ""} data-act="att" data-id="${d.id}|${r.from}|${dt}" aria-pressed="${on}" aria-label="${esc(e.name)}, ${fmtD(dt)}">${on ? "✓" : ""}</button></td>`;
           })
           .join("");
         const inp = (f) =>
           `<input class="dtr-in" id="att-${f}-${d.id}" data-dep="${d.id}" data-cut="${r.from}" data-f="${f}" inputmode="decimal" value="${a[f] || 0}" ${editable ? "" : "disabled"} aria-label="${esc(e.name)} ${f === "ot" ? "overtime hours" : f === "nd" ? "night hours" : "output"}">`;
         return `<tr><td><strong>${esc(e.name)}</strong><div class="small muted">${esc(d.project)} · ${esc(proj(d.project).site)}</div></td>${cells}<td class="n">${inp("ot")}</td><td class="n">${inp("nd")}</td><td class="n">${d.type === "PIECE" ? inp("units") + `<div class="small muted">${esc(d.unit)}</div>` : "—"}</td><td class="n">${x.worked}${x.holU ? `<div class="small muted">+${x.holU} holiday</div>` : ""}</td>${m ? `<td class="n">${peso(x.gross)}${x.topUp ? `<div class="small c-warn">incl. ${peso(x.topUp)} top-up</div>` : ""}${x.diff ? `<div class="small c-bad">incl. ${peso(x.diff)} wage-order difference</div>` : ""}</td>` : ""}</tr>`;
       })
       .join("") ||
     `<tr><td colspan="${dates.length + 6}" class="muted">Nobody is deployed in this cut-off.</td></tr>`
   }
   </tbody></table></div>
   <p class="small muted">Sundays are rest days (130%). Regular holidays pay 200% when worked and 100% when not, if the person worked the day before; special days pay 130%; overtime 125%; night shift +10% (Labor Code Arts. 86–94). Piece workers are topped up to the minimum for each day worked (Art. 101). The minimum follows the site and the date: ${Object.keys(
     MINW,
   )
     .map((k) => `${REGION_NAME[k]} ${peso(minWage(k, r.to).daily)}`)
     .join("; ")}.</p>`,
  )}`;
}

function dpBilling() {
  const r = depCut();
  if (!depMoney())
    return `${cutSeg()}<p class="note">Rates, costs and billing are hidden for this role.</p>`;
  const rows = depActiveIn(r.from, r.to).map((d) => ({
      d,
      x: depPay(S, d, r.from, r.to),
    })),
    codes = [...new Set(rows.map((o) => o.d.project))];
  return `${cutSeg()}${panel(
    "Billing by site",
    `${fmtD(r.from)} – ${fmtD(r.to)} · bill rate × man-days or output, overtime at 125%`,
    csvBtn("t-bill"),
    `<div class="tbl"><table id="t-bill"><thead><tr><th>Project · site</th><th class="n">Personnel</th><th class="n">Man-days</th><th class="n">Billable</th><th class="n">Labor cost</th><th class="n">Margin</th><th>Invoice</th></tr></thead><tbody>
   ${
     codes
       .map((c) => {
         const p = proj(c),
           os = rows.filter((o) => o.d.project === c),
           b = os.reduce((a, o) => a + o.x.bill, 0),
           k = os.reduce((a, o) => a + depCost(o.x), 0),
           key = c + "|" + r.from,
           inv = S.depBilled[key];
         return `<tr><td><strong>${esc(c)}</strong> ${esc(p.name)}<div class="small muted">${esc(client(p.cid).name)} · ${esc(p.site)}</div></td><td class="n">${os.length}</td><td class="n">${os.reduce((a, o) => a + o.x.worked, 0)}</td><td class="n">${peso(b)}</td><td class="n">${peso(k)}</td><td class="n">${peso(b - k)}<div class="small muted">${b ? Math.round(((b - k) / b) * 100) : 0}%</div></td><td>${inv ? chip("Billed " + inv, "good") : b && canInvoice() && r.to < today() ? `<button data-act="dep-bill" data-id="${key}">Prepare invoice</button>` : r.to >= today() ? chip("Cut-off open", "mut") : chip("Not billed", "warn")}</td></tr>`;
       })
       .join("") ||
     `<tr><td colspan="7" class="muted">Nothing to bill in this cut-off.</td></tr>`
   }
   </tbody></table></div><p class="small muted">Labor cost is gross pay, the employer's SSS, PhilHealth and Pag-IBIG, site allowances and the 13th-month accrual (1/12 of basic). Site works are invoiced under ATC WC120, so clients withhold 2%, not the 15% on consultancy fees.</p>`,
  )}
  ${panel(
    "Rate card",
    "Pay and bill rates of active deployments",
    "",
    `<div class="tbl"><table><thead><tr><th>Trade</th><th>Basis</th><th class="n">Pay rate</th><th class="n">Bill rate</th><th class="n">Markup</th></tr></thead><tbody>${S.deployments
      .filter((d) => d.status === "ACTIVE")
      .map(
        (d) =>
          `<tr><td>${esc(emp(d.emp).position)}<div class="small muted">${esc(emp(d.emp).name)} · ${esc(d.project)}</div></td><td>${d.type === "DAILY" ? "Per day" : "Per " + esc(d.unit)}</td><td class="n">${peso(d.rate)}</td><td class="n">${peso(d.bill)}</td><td class="n">${Math.round((d.bill / d.rate - 1) * 100)}%</td></tr>`,
      )
      .join(
        "",
      )}</tbody></table></div><p class="small muted">Put wage-order increases in every client contract. For construction and similar service contracts, RA 6727 §6 makes the client bear mandated wage increases.</p>`,
  )}`;
}

function dpCompliance() {
  const al = depAlerts(),
    co = S.company,
    canCo = can("dep.manage") || can("sys.admin");
  const rules = [
    [
      "Who is the employer",
      "If Milltech supplies people who work under the client's direction without Milltech's own capital, tools and supervision, it is labor-only contracting (Labor Code Art. 106; DO 174-17): the client becomes the employer. As a registered contractor Milltech stays the employer, and the client answers jointly for unpaid wages (Art. 109).",
    ],
    [
      "Project employment",
      "One written contract per project, naming it and its expected end (Art. 295). Moving someone from project to project without that can make them a regular employee. Report each project end to DOLE.",
    ],
    [
      "Minimum wage",
      "The site's region sets the minimum from each wage order's effective date (RA 6727): Western Visayas ₱550 for employers of more than 10; Central Visayas Class A ₱582 from 14 October 2026 (ROVII-27). A transfer can't lower anyone's pay.",
    ],
    [
      "Minimum wage earners",
      "Their basic, holiday, overtime and night pay are exempt from income tax (RA 9504), so payroll withholds nothing. Contributions still apply.",
    ],
    [
      "Site allowances",
      "A reasonable daily per diem paid while on assignment is not taxable pay and needs no receipts (RR 2-98 §2.78.1). Fixed allowances unrelated to the assignment are taxable.",
    ],
    [
      "Safety",
      "RA 11058 and DO 198-18: OSH orientation before work, PPE free of charge, and a safety officer per site by headcount and risk. Milltech and the client share these duties at the client's site.",
    ],
    [
      "Tax on billings",
      "Manpower and site works: 12% VAT on the invoice and 2% creditable withholding by the client (ATC WC120). Engineering consultancy: 15% (WC011).",
    ],
  ];
  return `<div class="grid">${panel(
    "Registrations",
    "DOLE and PCAB",
    "",
    `<div class="inbox"><div><div><strong>DOLE contractor registration (DO 174-17)</strong><div class="small muted">${co.dole?.status === "REGISTERED" ? `No. ${esc(co.dole.no)} · valid to ${fmtD(co.dole.until)}` : "With DOLE Regional Office VI, Iloilo City. Needs ₱5M substantial capital, own tools and equipment, and control over how the work is done."}</div></div>${co.dole?.status === "REGISTERED" ? chip("Registered", "good") : chip("Not registered", "bad")}</div>
   <div><div><strong>PCAB contractor's license (RA 4566)</strong><div class="small muted">${co.pcab?.status === "LICENSED" ? `No. ${esc(co.pcab.no)} · valid to ${fmtD(co.pcab.until)}` : "Needed if Milltech itself builds or installs: fabrication and erection, mechanical or electrical installation."}</div></div>${co.pcab?.status === "LICENSED" ? chip("Licensed", "good") : chip("Not licensed", "warn")}</div></div>
   ${canCo ? `<form id="regform" class="form"><label>Record<select id="rg-kind"><option value="dole">DOLE DO 174-17 registration</option><option value="pcab">PCAB license</option></select></label><label>Number<input id="rg-no"></label><label>Valid until<input id="rg-until" type="date"></label><label>&nbsp;<button type="submit">Save</button></label>${ui.rgErr ? `<p class="err wide">${esc(ui.rgErr)}</p>` : ""}</form>` : ""}`,
  )}
   ${panel("Open items", `${al.length} to resolve`, "", `<div class="inbox">${alertRows(al) || `<div><span class="muted">Nothing open.</span></div>`}</div>`)}</div>
  ${panel("The rules this page applies", "Sources are cited so the accountant and counsel can check them", "", `<div class="inbox">${rules.map((r) => `<div><div><strong>${r[0]}</strong><div class="small muted">${r[1]}</div></div></div>`).join("")}</div><p class="small muted">Wage orders change every year. The rates above come from published news of the orders; confirm each new order on the NWPC site before the payroll that first applies it.</p>`)}`;
}

function depForm() {
  const d = ui.depDraft,
    p = proj(d.project),
    mw = minWage(p.region, d.from || today()),
    free = S.employees.filter((e) => e.pay === "FIELD");
  const v = (k) => esc(d[k] ?? "");
  return `<form class="panel" id="depform"><div class="panel-h"><div><h2>Deploy personnel</h2><span class="muted">Minimum at ${esc(p.site)}: ${peso(mw.daily)} a day (${esc(mw.src)})</span></div></div><div class="form">
   <label>Person<select id="dp-emp">${free.map((e) => `<option value="${e.id}"${e.id === d.emp ? " selected" : ""}>${esc(e.name)} · ${esc(e.position)}</option>`).join("")}<option value="NEW"${d.emp === "NEW" ? " selected" : ""}>New hire…</option></select></label>
   ${d.emp === "NEW" ? `<label>Full name<input id="dp-name" value="${v("name")}"></label><label>Trade<input id="dp-trade" value="${v("trade")}" placeholder="e.g. Welder (SMAW NC II)"></label>` : ""}
   <label>Project · site<select id="dp-project">${S.projects.map((x) => `<option value="${x.code}"${x.code === d.project ? " selected" : ""}>${esc(siteCode(x))} · ${esc(x.site)}</option>`).join("")}</select></label>
   <label>Pay basis<select id="dp-type"><option value="DAILY"${d.type === "DAILY" ? " selected" : ""}>Daily rate</option><option value="PIECE"${d.type === "PIECE" ? " selected" : ""}>Piece rate</option></select></label>
   <label>${d.type === "DAILY" ? "Daily rate (₱)" : "Rate per unit (₱)"}<input id="dp-rate" inputmode="decimal" value="${v("rate")}" style="text-align:right"></label>
   ${d.type === "PIECE" ? `<label>Unit<input id="dp-unit" value="${v("unit")}" placeholder="e.g. duct section"></label><label>Standard output a day<input id="dp-std" inputmode="decimal" value="${v("std")}" placeholder="from a time-and-motion study" style="text-align:right"></label>` : ""}
   <label>Bill rate to client (₱)<input id="dp-bill" inputmode="decimal" value="${v("bill")}" style="text-align:right"></label>
   <label>Site allowance a day (₱)<input id="dp-pd" inputmode="decimal" value="${v("pd")}" style="text-align:right"></label>
   <label>Starts<input type="date" id="dp-from" value="${v("from")}"></label><label>Expected end<input type="date" id="dp-to" value="${v("to")}"></label>
   <label class="wide" style="flex-direction:row;gap:8px;align-items:center;color:var(--ink)"><input type="checkbox" id="dp-contract" ${d.contract ? "checked" : ""} style="width:16px;height:16px;min-width:0;min-height:0"> A project employment contract for this project is signed</label>
  </div>${ui.depErr ? `<p class="err">${esc(ui.depErr)}</p>` : ""}<div class="row"><button class="pri" type="submit">Deploy</button><button type="button" class="ghost" data-act="dep-cancel">Cancel</button></div></form>`;
}

function readDep() {
  const d = ui.depDraft,
    g = (x) => document.getElementById("dp-" + x);
  if (!d) return;
  [
    "emp",
    "name",
    "trade",
    "project",
    "type",
    "rate",
    "unit",
    "std",
    "bill",
    "pd",
    "from",
    "to",
  ].forEach((k) => {
    const el = g(k);
    if (el) d[k] = el.value;
  });
  d.contract = !!g("contract")?.checked;
}

export {
  DEP_TABS,
  DEP_SUB,
  depCut,
  depActiveIn,
  syncDraft,
  depCost,
  depAlerts,
  cutSeg,
  alertRows,
  dpOverview,
  dpPersonnel,
  dpAttendance,
  dpBilling,
  dpCompliance,
  depForm,
  readDep,
};
